import React from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import useSplunkLogger from '../../common/hooks/useSplunkLogger';
import { splunk_message } from '../../common/splunk_constant';
import DisplayErrorBoundary from '../../container/error/displayErrorBoundary';
import type { ErrorBoundaryContainerProps, ErrorBoundaryContainerState, ErrorBoundaryProps } from '../../container/error/interfaces/errorBoundary';

class ErrorBoundaryContainer extends React.Component<ErrorBoundaryContainerProps, ErrorBoundaryContainerState> {
  constructor(props: ErrorBoundaryContainerProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryContainerState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    this.props?.splunkLogger?.({
      logType: 'error',
      data: {
        ...splunk_message.ERROR.CLIENT_ERROR_BOUNDARY_ERROR,
        err: {
          message: error?.message,
          name: error?.name,
          componentStack: errorInfo?.componentStack,
        },
      },
    });
    console.error(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      //TODO Need to put a new ux for standard error later
      return (
        <>
        <DisplayErrorBoundary errMsg={this.props.genericErrorMessage} />
        </>
      );
    }

    return this.props.children;
  }
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const { splunkLogger } = useSplunkLogger();
  const { t } = useTranslation();

  const genericErrorMessage = t('errorBoundary.genericErrorMessage', {
    defaultValue: 'Something went wrong. Please try again later or return to the home page.',
  });

  return (
    <ErrorBoundaryContainer splunkLogger={splunkLogger} genericErrorMessage={genericErrorMessage}>
      {children}
    </ErrorBoundaryContainer>
  );
};

export default ErrorBoundary;
